// Predefined time windows for trip scheduling
export const timeWindows = [
  { value: '06:00-08:00', label: 'Early Morning (6:00 AM - 8:00 AM)' },
  { value: '08:00-10:00', label: 'Morning (8:00 AM - 10:00 AM)' },
  { value: '10:00-12:00', label: 'Late Morning (10:00 AM - 12:00 PM)' },
  { value: '12:00-14:00', label: 'Early Afternoon (12:00 PM - 2:00 PM)' },
  { value: '14:00-16:00', label: 'Afternoon (2:00 PM - 4:00 PM)' },
  { value: '16:00-18:00', label: 'Late Afternoon (4:00 PM - 6:00 PM)' },
  { value: '18:00-20:00', label: 'Evening (6:00 PM - 8:00 PM)' },
  { value: '20:00-22:00', label: 'Late Evening (8:00 PM - 10:00 PM)' }
];

// Helper function to parse time window
export const parseTimeWindow = (timeWindow) => {
  if (!timeWindow) return { start: null, end: null };
  const [start, end] = timeWindow.split('-');
  return { start, end };
};

// Helper function to format time window for display
export const formatTimeWindow = (startTime, endTime) => {
  if (!startTime || !endTime) return 'Time TBD';
  
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  const start = formatTime(startTime);
  const end = formatTime(endTime);
  return `${start} - ${end}`;
}; 

// Travel durations in minutes for different segments
const TRAVEL_DURATIONS = {
  'NairobiToArusha': 5 * 60, // 5 hours = 300 minutes
  'ArushaToMoshi': 1 * 60 + 50, // 1 hour 50 minutes = 110 minutes
  'NairobiToMoshi': 6 * 60, // 6 hours = 360 minutes
  'MoshiToArusha': 1 * 60 + 50, // 1 hour 50 minutes = 110 minutes (reverse)
  'ArushaToNairobi': 5 * 60, // 5 hours = 300 minutes (reverse)
  'MoshiToNairobi': 6 * 60, // 6 hours = 360 minutes (reverse)
};

// Calculate estimated arrival time based on departure time and segment
export function calculateArrivalTime(departureTime, segment) {
  if (!departureTime || !segment || !TRAVEL_DURATIONS[segment]) {
    return null;
  }

  // Parse departure time (assuming format like "08:00")
  const [hours, minutes] = departureTime.split(':').map(Number);
  const departureDate = new Date();
  departureDate.setHours(hours, minutes, 0, 0);

  // Add travel duration
  const arrivalDate = new Date(departureDate.getTime() + TRAVEL_DURATIONS[segment] * 60 * 1000);

  // Format arrival time
  const arrivalHours = arrivalDate.getHours().toString().padStart(2, '0');
  const arrivalMinutes = arrivalDate.getMinutes().toString().padStart(2, '0');
  
  return `${arrivalHours}:${arrivalMinutes}`;
}

// Calculate departure time for a specific segment based on the main trip departure
export function calculateSegmentDepartureTime(mainDepartureTime, segment) {
  if (!mainDepartureTime || !segment) {
    return null;
  }

  // For intermediate segments, calculate when the vehicle arrives at that point
  if (segment === 'ArushaToMoshi') {
    // Vehicle departs Nairobi at mainDepartureTime, arrives in Arusha 5 hours later
    return calculateArrivalTime(mainDepartureTime, 'NairobiToArusha');
  } else if (segment === 'ArushaToNairobi') {
    // Vehicle departs Moshi at mainDepartureTime, arrives in Arusha 1h50m later
    return calculateArrivalTime(mainDepartureTime, 'MoshiToArusha');
  } else {
    // For full routes or origin segments, use the main departure time
    return mainDepartureTime;
  }
}

// Calculate arrival time for a specific segment, accounting for intermediate stops
export function calculateSegmentArrivalTime(mainDepartureTime, segment) {
  if (!mainDepartureTime || !segment) {
    return null;
  }

  // Get the actual departure time for this segment
  const segmentDepartureTime = calculateSegmentDepartureTime(mainDepartureTime, segment);
  
  if (!segmentDepartureTime) {
    return null;
  }

  // Calculate arrival time from the segment's departure time
  return calculateArrivalTime(segmentDepartureTime, segment);
}

// Get travel duration in a readable format
export function getTravelDuration(segment) {
  if (!segment || !TRAVEL_DURATIONS[segment]) {
    return null;
  }

  const durationMinutes = TRAVEL_DURATIONS[segment];
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
} 